/** @module src/task/util */
define([
	"csi/access/crews",
	"csi/config",
	"csi/core/extendedPathService",
	"csi/def",
	"csi/sessionConstants",
	"csi/types",
	"csi/xml",
], function(crews, config, {parseExtendedPath}, def, sessionConstants, types, xml) {

	let _dueDateThreshold;

	/**
	 * Enumeration for due date classifications
	 * @enum {string}
	 */
	const TaskDueDateClassification = {
		LATER: "Later",
		NEXT_WEEK: "Next",
		OVERDUE: "Overdue",
		TODAY: "Today",
		UNSCHEDULED: "Unscheduled",
		UPCOMING: "Upcoming"
	};

	/**
	 * Return true if current user can complete the given task
	 * Used only for camunda
	 * @param {Node} task
	 * @return {boolean}
	 */
	function canCompleteTask(task) {
		return isTaskActive(task) && (task.Assignee === sessionConstants.userUrl);
	}

	/**
	 * Enable editing if the user is Workflow Administrator or Assignee on the task
	 * @param {Context} ctx
	 * @return {boolean}
	 */
	function canEditDueDateOrPriority(ctx) {
		if (isWorkflowAdmin(ctx)) {
			return true;
		}

		const taskNode = ctx.getNode();
		return sessionConstants.userUrl == taskNode.Assignee;
	}

	/**
	 * Return classification for task due date value
	 * @param {Date|null} dt
	 * @param {boolean} [forColorCoding] - color coding of due dates has an additional classification for threshold plus a week
	 * @return {TaskDueDateClassification}
	 */
	function getDueDateClassification(dt, forColorCoding) {
		if (!dt) {
			return TaskDueDateClassification.UNSCHEDULED;
		}

		const diff = types.getDateDifference(dt);

		if (diff === 0) {
			return TaskDueDateClassification.TODAY;
		} else if (diff < 0) {
			return TaskDueDateClassification.OVERDUE;
		}

		const threshold = getDueDateThreshold();

		if (diff <= threshold) {
			return TaskDueDateClassification.UPCOMING;
		}

		if (forColorCoding && (diff <= threshold + 7)) {
			return TaskDueDateClassification.NEXT_WEEK;
		}

		return TaskDueDateClassification.LATER;
	}

	/**
	 * Return number of days before today that a task is considered upcoming
	 * @return {number}
	 */
	function getDueDateThreshold() {
		if (_dueDateThreshold == null) {
			var value = config.getClassSetting("CamundaTask", "DueDateThreshold");
			_dueDateThreshold = Number(xml.enumGetText(value));
		}

		return _dueDateThreshold;
	}

	/**
	 * Return true if the given task is started, but neither completed nor cancelled
	 * Used only for camunda
	 * @param {Node} task
	 * @return {boolean}
	 */
	function isTaskActive(task) {
		return task.ActivityState == "CamundaActivityState:Started";
	}

	/**
	 * Return true if user has Workflow Admin rights on node in context
	 * @param {Context} ctx
	 * @return {boolean}
	 */
	function isWorkflowAdmin(ctx) {
		return crews.isInRoles(["Workflow Administrator"], ctx.getContextNode());
	}

	/**
	 * Return if configured view for task definition is a property view
	 * @param {Node} taskDefinition
	 * @return {boolean}
	 */
	function taskDefinitionHasPropertyView(taskDefinition) {
		const viewDefPath = taskDefinition.ViewDefinitionPath;

		if (!viewDefPath) {
			return false;
		}

		const ep = parseExtendedPath(viewDefPath);
		const rc = ep.getEndClass();
		const viewDef = def.getClassViewDef(rc, def.decomposeViewKey(taskDefinition.ViewStoreId).id);

		return viewDef && (viewDef.getType() === "Property");
	}
	return {
		canCompleteTask,
		canEditDueDateOrPriority,
		getDueDateClassification,
		getDueDateThreshold,
		isTaskActive,
		isWorkflowAdmin,
		taskDefinitionHasPropertyView,
	}
});

